<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <div
      v-if="permReady && !can.view"
      class="mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm p-10 text-center"
      role="alert"
    >
      <div
        class="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 text-red-600 grid place-items-center"
      >
        <svg viewBox="0 0 24 24" class="w-6 h-6">
          <path fill="currentColor" d="M11 7h2v6h-2zm0 8h2v2h-2z" />
          <path fill="currentColor" d="M1 21h22L12 2 1 21z" />
        </svg>
      </div>
      <h2 class="font-bold text-gray-900 mb-1">403 — You don't have permission</h2>
      <p class="text-gray-600">Please contact the administrator to enable access.</p>
    </div>

    <template v-else-if="permReady && can.view">
      <h1 class="text-[22px] sm:text-[24px] font-bold text-gray-900 mb-6">Permissions</h1>

      <div class="flex items-center justify-between gap-4 mb-4">
        <div class="relative w-64 h-9">
          <span class="absolute inset-y-0 left-3 flex items-center pointer-events-none z-10">
            <svg
              class="h-4 w-4 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-width="2"
                d="M21 21l-4.3-4.3M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
              />
            </svg>
          </span>
          <input
            v-model="search"
            type="text"
            placeholder="Search"
            class="search-input w-full h-full rounded-lg border border-gray-300 pr-3 text-sm focus:outline-none"
          />
        </div>

        <button
          v-if="can.create"
          type="button"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-full text-white font-bold text-sm shadow-sm hover:opacity-95"
          :style="{ backgroundColor: '#5B9AC5' }"
          :disabled="loading"
          @click="openAdd"
        >
          + New user
        </button>
      </div>

      <div class="mb-4">
        <div v-if="loading" class="text-sm text-gray-600">Loading...</div>
        <div v-else-if="errorMsg" class="text-sm text-red-600">{{ errorMsg }}</div>
      </div>

      <DynamicTable
        :rows="rows"
        :columns="columns"
        :rows-per-page="rows.length || 1000"
        :rows-per-page-options="[rows.length || 1000]"
        :show-header="false"
        :external-search="search"
      >
        <template #action="{ row }">
          <div class="flex items-center justify-center gap-3 text-[13px]">
            <template v-if="row.id !== 1">
              <button
                v-if="can.edit"
                class="flex items-center gap-1 text-gray-700"
                @click="editRow(row)"
              >
                <svg viewBox="0 0 24 24" class="w-4 h-4">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm18-11.5a1 1 0 0 0 0-1.41L18.66 3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75L21 5.75Z"
                    fill="currentColor"
                  />
                </svg>
                <span>Edit</span>
              </button>
              <button
                v-if="can.delete"
                class="flex items-center gap-1 text-black"
                @click="openDelete(row)"
              >
                <i class="fas fa-trash-alt text-red-600"></i>
                <span>Delete</span>
              </button>
            </template>
          </div>
        </template>
      </DynamicTable>

      <!-- Add Modal -->
      <teleport to="body">
        <div v-if="showAdd" class="fixed inset-0 z-[1000] flex items-center justify-center">
          <div class="absolute inset-0 bg-black/50" @click="closeAdd"></div>
          <div
            class="relative bg-white w-[92%] max-w-[460px] rounded-2xl shadow-lg p-6 sm:p-8"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              class="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              @click="closeAdd"
            >
              ✕
            </button>
            <h3 class="text-center text-xl font-semibold text-gray-900 mt-2 mb-6">Add User</h3>

            <form @submit.prevent="submitAdd" autocomplete="off" class="space-y-3">
              <input type="text" style="display: none" />
              <input type="password" style="display: none" />

              <!-- Name -->
              <div>
                <input
                  v-model="addForm.name"
                  type="text"
                  placeholder="Enter Name"
                  class="w-full h-10 rounded-xl border px-3 text-sm focus:outline-none"
                  :class="errors.name ? 'border-red-400' : 'border-gray-300'"
                />
                <p v-if="errors.name" class="text-red-500 text-xs mt-1">{{ errors.name }}</p>
              </div>

              <!-- Email -->
              <div>
                <input
                  v-model="addForm.email"
                  type="email"
                  placeholder="Enter Email"
                  class="w-full h-10 rounded-xl border px-3 text-sm focus:outline-none"
                  :class="errors.email ? 'border-red-400' : 'border-gray-300'"
                />
                <p v-if="errors.email" class="text-red-500 text-xs mt-1">{{ errors.email }}</p>
              </div>

              <!-- Phone with Country Code Dropdown -->
              <div>
                <div class="flex gap-1">
                  <!-- Country Code Combobox -->
                  <div class="relative w-36" ref="countryCodeWrapper">
                    <input
                      v-model="countrySearchText"
                      type="text"
                      placeholder="Country Code"
                      class="w-full h-10 rounded-xl border px-3 text-sm focus:outline-none"
                      :class="errors.phone ? 'border-red-400' : 'border-gray-300'"
                      @focus="openCountryDropdown"
                      @input="onCountryInput"
                      @keydown.down.prevent="moveFocus(1)"
                      @keydown.up.prevent="moveFocus(-1)"
                      @keydown.enter.prevent="selectFocused"
                      @keydown.escape="closeCountryDropdown"
                      autocomplete="off"
                    />
                    <!-- Dropdown -->
                    <div
                      v-if="countryDropdownOpen"
class="absolute left-0 top-[42px] z-[2000] w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"                    >
                      <!-- Loading -->
                      <div
                        v-if="countriesLoading"
                        class="px-3 py-2 text-xs text-gray-400 text-center"
                      >
                        Loading countries...
                      </div>
                      <!-- List -->
                      <ul
                        v-else
                        class="max-h-52 overflow-y-auto"
                        ref="countryList"
                      >
                        <li
                          v-if="filteredCountries.length === 0"
                          class="px-3 py-2 text-xs text-gray-400 text-center"
                        >
                          No results
                        </li>
                        <li
                          v-for="(country, idx) in filteredCountries"
                          :key="country.idd_root + country.name"
                          class="flex items-center gap-1 px-2 py-2 text-xs cursor-pointer transition-colors"
                          :class="focusedIndex === idx ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-800'"
                        >
                          <span class="shrink-0 font-mono text-[11px] text-gray-500 w-9">
                            {{ country.dialCode }}
                          </span>

                          <span class="flex-1 min-w-0 whitespace-normal leading-tight">
                            {{ country.name }}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Phone number -->
                  <input
                    v-model="addForm.phone"
                    type="text"
                    placeholder="Enter Phone number"
                    class="flex-1 h-10 rounded-xl border px-3 text-sm focus:outline-none"
                    :class="errors.phone ? 'border-red-400' : 'border-gray-300'"
                  />
                </div>
                <p v-if="errors.phone" class="text-red-500 text-xs mt-1">{{ errors.phone }}</p>
              </div>

              <!-- Password -->
              <div>
                <div class="relative">
                  <input
                    v-model="addForm.password"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="Enter Password"
                    autocomplete="new-password"
                    class="w-full h-10 rounded-xl border px-3 pr-10 text-sm focus:outline-none"
                    :class="errors.password ? 'border-red-400' : 'border-gray-300'"
                  />
                  <button
                    type="button"
                    tabindex="-1"
                    class="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                    @click="showPassword = !showPassword"
                  >
                    <svg
                      v-if="!showPassword"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path
                        d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.88"
                      />
                      <path
                        d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.8 21.8 0 0 1-3.24 4.23"
                      />
                      <path d="m1 1 22 22" />
                    </svg>
                  </button>
                </div>
                <p v-if="errors.password" class="text-red-500 text-xs mt-1">
                  {{ errors.password }}
                </p>
              </div>

              <button
                type="submit"
                :disabled="submitting"
                class="w-full h-10 rounded-full text-white font-semibold shadow-sm hover:opacity-95 disabled:opacity-60"
                :style="{ backgroundColor: '#5B9AC5' }"
              >
                {{ submitting ? 'Saving...' : 'Add' }}
              </button>
            </form>
          </div>
        </div>
      </teleport>

      <!-- Delete Modal -->
      <DeleteModal
        v-model="showDelete"
        :title="deleteTitle"
        :message="deleteMessage"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </template>
  </div>
</template>

<script>
import DynamicTable from '@/components/DynamicTable.vue'
import DeleteModal from '@/components/DeleteModal.vue'
import Swal from 'sweetalert2'
import api from '@/api'
import { loadMyPermissions, hasAny } from '@/utils/permissions'

export default {
  name: 'Permissions',
  components: { DynamicTable, DeleteModal },

  data() {
    return {
      permReady: false,
      can: { view: false, create: false, edit: false, delete: false },

      search: '',
      rows: [],
      columns: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'action', label: 'Action' },
      ],

      showAdd: false,
      showPassword: false,
      addForm: { name: '', email: '', country_code: '', phone: '', password: '' },
      errors: {},
      submitting: false,

      // Country code dropdown
      allCountries: [],
      countriesLoading: false,
      countriesLoaded: false,
      countrySearchText: '',
      countryDropdownOpen: false,
      focusedIndex: -1,

      showDelete: false,
      rowToDelete: null,

      loading: false,
      errorMsg: '',
    }
  },

  computed: {
    deleteTitle() {
      return 'Are you sure to'
    },
    deleteMessage() {
      return `delete ${this.rowToDelete?.name ?? 'this User'}?`
    },

    filteredCountries() {
      const q = this.countrySearchText.trim().toLowerCase()
      if (!q) return this.allCountries
      return this.allCountries.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dialCode.replace('+', '').includes(q) ||
          c.dialCode.includes(q)
      )
    },
  },

  async created() {
    await this.loadPermissions()
    if (this.can.view) await this.fetchRows()
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },

  methods: {
    // ─── Permissions ───────────────────────────────────────────
    async loadPermissions() {
      try {
        await loadMyPermissions(false)
        const { data } = await api.get('/accounting/permissions/me')
        const canObj = data?.can || {}

        if (data?.user?.id === 1) {
          this.can = { view: true, create: true, edit: true, delete: true }
          this.permReady = true
          return
        }

        const othersPerms = canObj?.others?.permissions
        if (othersPerms && typeof othersPerms === 'object') {
          this.can.view = !!(othersPerms.view ?? othersPerms.allow ?? false)
          this.can.create = !!(othersPerms.create ?? othersPerms.allow ?? false)
          this.can.edit = !!(othersPerms.edit ?? othersPerms.allow ?? false)
          this.can.delete = !!(othersPerms.delete ?? othersPerms.allow ?? false)
        }

        if (!this.can.view) this.can.view = !!(canObj?.permissions?.view?.allow ?? false)
        if (!this.can.edit) this.can.edit = !!(canObj?.permissions?.edit?.allow ?? false)

        if (!this.can.view) this.can.view = hasAny(['others.permissions', 'permissions.view'])
        if (!this.can.edit) this.can.edit = hasAny(['permissions.edit'])
      } catch (e) {
        this.can.view = hasAny(['others.permissions', 'permissions.view'])
        this.can.create = hasAny(['others.permissions'])
        this.can.edit = hasAny(['others.permissions', 'permissions.edit'])
        this.can.delete = hasAny(['others.permissions'])
      } finally {
        this.permReady = true
      }
    },

    // ─── Rows ───────────────────────────────────────────────────
    async fetchRows() {
      this.loading = true
      this.errorMsg = ''
      try {
        const { data } = await api.get('/accounting/accounting-users')
        this.rows = Array.isArray(data) ? data : Array.isArray(data?.users) ? data.users : []
      } catch (e) {
        this.rows = []
        this.errorMsg = e?.response?.data?.message || 'Failed to load users.'
      } finally {
        this.loading = false
      }
    },

    // ─── Country code dropdown ──────────────────────────────────
    async loadCountries() {
      if (this.countriesLoaded || this.countriesLoading) return
      this.countriesLoading = true
      try {
        // restcountries API — free, no auth, returns name + idd (dial codes)
        const res = await fetch(
          'https://restcountries.com/v3.1/all?fields=name,idd'
        )
        const raw = await res.json()

        const list = []
        for (const c of raw) {
          const root = c.idd?.root
          if (!root) continue
          const suffixes = c.idd?.suffixes || ['']
          for (const suf of suffixes) {
            const dialCode = root + suf
            // skip entries that produce very long or invalid codes
            if (dialCode.length > 6) continue
            list.push({
              name: c.name?.common || '',
              dialCode,
            })
          }
        }

        // sort: shorter dial codes first, then alphabetically
        list.sort((a, b) => {
          if (a.dialCode.length !== b.dialCode.length)
            return a.dialCode.length - b.dialCode.length
          return a.name.localeCompare(b.name)
        })

        this.allCountries = list
        this.countriesLoaded = true
      } catch (e) {
        console.error('Failed to load countries', e)
      } finally {
        this.countriesLoading = false
      }
    },

    async openCountryDropdown() {
      this.countryDropdownOpen = true
      this.focusedIndex = -1
      await this.loadCountries()
    },

    closeCountryDropdown() {
      this.countryDropdownOpen = false
      this.focusedIndex = -1
      // If user typed something but didn't pick — keep whatever is in the input as country_code
      if (this.countrySearchText && !this.addForm.country_code) {
        this.addForm.country_code = this.countrySearchText.trim()
      }
    },

    onCountryInput() {
      this.countryDropdownOpen = true
      this.focusedIndex = -1
      // Clear the stored code while user is typing so it re-resolves on pick
      this.addForm.country_code = ''
    },

    selectCountry(country) {
      this.addForm.country_code = country.dialCode
      this.countrySearchText = country.dialCode
      this.countryDropdownOpen = false
      this.focusedIndex = -1
    },

    moveFocus(dir) {
      if (!this.filteredCountries.length) return
      this.focusedIndex = Math.max(
        0,
        Math.min(this.filteredCountries.length - 1, this.focusedIndex + dir)
      )
      // Scroll into view
      this.$nextTick(() => {
        const ul = this.$refs.countryList
        if (!ul) return
        const li = ul.children[this.focusedIndex]
        if (li) li.scrollIntoView({ block: 'nearest' })
      })
    },

    selectFocused() {
      if (this.focusedIndex >= 0 && this.filteredCountries[this.focusedIndex]) {
        this.selectCountry(this.filteredCountries[this.focusedIndex])
      }
    },

    handleOutsideClick(e) {
      const wrapper = this.$refs.countryCodeWrapper
      if (wrapper && !wrapper.contains(e.target)) {
        this.countryDropdownOpen = false
      }
    },

    // ─── Add Modal ──────────────────────────────────────────────
    openAdd() {
      if (!this.can.create) return
      this.addForm = { name: '', email: '', country_code: '', phone: '', password: '' }
      this.countrySearchText = ''
      this.errors = {}
      this.showPassword = false
      this.showAdd = true
      document.body.style.overflow = 'hidden'
    },

    closeAdd() {
      this.showAdd = false
      this.countryDropdownOpen = false
      document.body.style.overflow = ''
    },

    validateAdd() {
      const e = {}
      if (!this.addForm.name?.trim()) e.name = 'Name is required.'
      if (!this.addForm.email?.trim()) e.email = 'Email is required.'
      if (!this.addForm.phone?.trim()) e.phone = 'Phone is required.'
      if (!this.addForm.password) e.password = 'Password is required.'
      else if (this.addForm.password.length < 6)
        e.password = 'Password must be at least 6 characters.'
      this.errors = e
      return Object.keys(e).length === 0
    },

    async submitAdd() {
      if (!this.can.create || !this.validateAdd()) return
      this.submitting = true
      try {
        await api.post('/accounting/accounting-users', {
          name: this.addForm.name.trim(),
          email: this.addForm.email.trim(),
          country_code: this.addForm.country_code?.trim() || null,
          phone: this.addForm.phone.trim(),
          role: 'admin',
          password: this.addForm.password,
        })
        this.closeAdd()
        Swal.fire({
          icon: 'success',
          title: 'User added successfully.',
          toast: true,
          position: 'top',
          timer: 1400,
          showConfirmButton: false,
        })
        await this.fetchRows()
      } catch (e) {
        const msg = e?.response?.data?.message || e?.response?.data?.error || 'Failed to add user.'
        const errs = e?.response?.data?.errors
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: msg,
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        })
        if (errs) {
          if (errs.name?.[0]) this.errors.name = errs.name[0]
          if (errs.email?.[0]) this.errors.email = errs.email[0]
          if (errs.phone?.[0]) this.errors.phone = errs.phone[0]
          if (errs.password?.[0]) this.errors.password = errs.password[0]
        }
      } finally {
        this.submitting = false
      }
    },

    // ─── Edit / Delete ──────────────────────────────────────────
    editRow(row) {
      if (row.id === 1 || !this.can.edit) return
      this.$router.push({ name: 'PermissionsEdit', params: { id: row.id } })
    },

    openDelete(row) {
      if (row.id === 1 || !this.can.delete) return
      this.rowToDelete = row
      this.showDelete = true
      document.body.style.overflow = 'hidden'
    },

    cancelDelete() {
      this.showDelete = false
      this.rowToDelete = null
      document.body.style.overflow = ''
    },

    async confirmDelete() {
      if (!this.rowToDelete || !this.can.delete) return
      try {
        await api.delete(`/accounting/accounting-users/${this.rowToDelete.id}`)
        Swal.fire({
          icon: 'success',
          title: 'Deleted successfully.',
          toast: true,
          position: 'top',
          timer: 1400,
          showConfirmButton: false,
        })
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: e?.response?.data?.message || e?.response?.data?.error || 'Failed to delete.',
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        })
      } finally {
        this.cancelDelete()
        await this.fetchRows()
      }
    },
  },
}
</script>

<style scoped>
.search-input {
  padding-left: 2.4rem !important;
  line-height: 2.25rem;
}
:deep(td) {
  @apply text-base;
}

/* Chrome / Edge / Safari */
input[type="password"]::-webkit-credentials-auto-fill-button,
input[type="password"]::-webkit-textfield-decoration-container,
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none !important;
}
input[type="password"]::-ms-reveal {
  display: none !important;
}
input[type="password"] {
  -webkit-text-security: disc;
}
</style>
